import React, { useState } from 'react';
import { StaffReport, ReportType } from '../../../types/timeReport';
import { formatHours } from '../../../utils/timeReportCalculations';
import DailyBreakdown from './DailyBreakdown';

interface StaffReportTableProps {
  reports: StaffReport[];
  reportType: ReportType;
  startDate: Date;
  endDate: Date;
  onEditShift: (shiftId: string) => void;
}

const StatusBadge: React.FC<{ status: 'complete' | 'needs_review' }> = ({ status }) => {
  const styles = {
    complete: 'bg-green-100 text-green-800',
    needs_review: 'bg-yellow-100 text-yellow-800',
  };

  const labels = {
    complete: 'Complete',
    needs_review: 'Needs Review',
  };

  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status]}`}>
      {labels[status]}
    </span>
  );
};

const StaffReportTable: React.FC<StaffReportTableProps> = ({
  reports,
  reportType,
  startDate,
  endDate,
  onEditShift,
}) => {
  const [expandedStaffId, setExpandedStaffId] = useState<string | null>(null);

  const toggleExpand = (staffId: string) => {
    setExpandedStaffId(expandedStaffId === staffId ? null : staffId);
  };

  if (reports.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="text-center py-12">
          <p className="text-gray-500 text-lg">No time log data found for the selected period.</p>
          <p className="text-gray-400 text-sm mt-2">Try adjusting the date range or staff filter.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Staff Member
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Total Hours
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Days Worked
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Avg Hours/Day
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Shifts
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Incomplete
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Status
            </th>
            <th scope="col" className="relative px-6 py-3">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {reports.map((report) => (
            <React.Fragment key={report.staff_id}>
              <tr
                className="hover:bg-gray-50 cursor-pointer transition-colors"
                onClick={() => toggleExpand(report.staff_id)}
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    {report.staff_photo ? (
                      <img
                        src={report.staff_photo}
                        alt={report.staff_name}
                        className="h-10 w-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                        <span className="text-gray-500 font-medium text-sm">
                          {report.staff_name.charAt(0)}
                        </span>
                      </div>
                    )}
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">
                        {report.staff_name}
                      </div>
                      <div className="text-sm text-gray-500">{report.staff_role}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-semibold text-gray-900">
                    {formatHours(report.total_hours)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{report.days_worked}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    {formatHours(report.avg_hours)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{report.shift_count}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {report.incomplete_count > 0 ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                      {report.incomplete_count}
                    </span>
                  ) : (
                    <span className="text-sm text-gray-400">-</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <StatusBadge
                    status={report.incomplete_count === 0 ? 'complete' : 'needs_review'}
                  />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleExpand(report.staff_id);
                    }}
                    className="text-indigo-600 hover:text-indigo-900"
                  >
                    {expandedStaffId === report.staff_id ? '▼ Collapse' : '▶ Expand'}
                  </button>
                </td>
              </tr>

              {/* Expandable Row with Daily Breakdown */}
              {expandedStaffId === report.staff_id && (
                <tr>
                  <td colSpan={8} className="px-6 py-4 bg-gray-50">
                    <DailyBreakdown
                      staffName={report.staff_name}
                      shifts={report.shifts}
                      reportType={reportType}
                      startDate={startDate}
                      endDate={endDate}
                      onEditShift={onEditShift}
                    />
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default StaffReportTable;
